import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  createStyles,
  makeStyles,
  Theme,
  useTheme,
} from '@material-ui/core/styles';
import {
  Tab,
  Tabs,
  Typography,
  Selector,
  ISelectorItem,
  Modal,
} from '@thingsw/pitta-design-system';
import { useTranslation } from 'react-i18next';

import { PlaybackPanel } from '../components/cameras/PlaybackPanel';
import { useDispatch, useSelector } from 'react-redux';
import {
  CAMERA,
  loadCamera,
  loadCameras,
  loadFirmwareConfig,
  loadFirmwareSettings,
  setCameraTimeSync,
} from '../features/Camera/slice';
import { RootState } from '../features/store';
import clsx from 'clsx';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import { CameraSettingPanel } from '../components/cameras/CameraSettingPanel';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { ReportPanel } from '../components/cameras/ReportPanel';
import { GPSTrackingPanel } from '../components/gps-tracking/GPSTrackingPanel';

import { FileUploadModal } from '../components/FileUploadModal';
import { ScreenDefaultProps } from '../hoc/withViewerTemplate';
import {
  clearDownloadVOD,
  loadVODUploadList,
  VOD,
} from '../features/VOD/slice';
import _ from 'lodash';
import { USER } from '../features/User/slice';
import { openToast } from '../features/Toast/slice';
import { AutoSizer } from 'react-virtualized';
import Scrollbars from 'react-custom-scrollbars';
import { THEME } from '../features/Theme/slice';
import { FileDownloadModal } from '../components/modals/FileDownloadModal';
import {
  ICameraInfo,
  Webviewer,
  SemanticDarkColors,
  UPLOADING_QUEUE_UPDATE_INTERVAL,
  PSN650,
  PermissionProps,
  ENABLED_CLOUD_OLD_MODELS,
  OLD_MODELS,
  AvailableFws,
  LightColors,
} from '@thingsw/pitta-modules';
import { LiveViewPanelWrapper } from '../components/cameras/LiveViewPanelWrapper';
import { LIVEVIEW } from '../features/LiveView/slice';
import { setCameraInfo } from '../features/GPS/slice';
import CloudStorageTourModal from '../components/modals/CloudStorageTourModal';
import { getCloudStorageUsage } from '../apis/CloudStorage';
import { NotificationSettingPanel } from '../components/renewal2/NotificationSettingPanel';
import { PAYMENT } from '../features/Payment/slice';
import useRole from '../hooks/useRole';
import { FleetaCameraMenu } from '../components/fleeta/FleetaCameraMenu';
import { EditProfileModal } from '../components/uxui/EditProfileModal';
import UnpairBatteryModal from '../components/modals/UnpairBatteryModal';
import { useQuery } from '@tanstack/react-query';
import { findNewFw } from '../utils/Firmware';
import { getPatialUnsupportModel } from '../utils/getPatialUnsupportModel';
import ErrorIcon from '@material-ui/icons/Error';

export type ScreenMode =
  | 'playback'
  | 'live-view'
  | 'notifications'
  | 'camera-cloud-storage'
  | 'report'
  | 'gps-tracking'
  | 'settings'
  | 'cloud-storage';

export interface CameraPlaybackScreenProps {
  mode: ScreenMode;
  psn: string;
  onRename?: (camera: ICameraInfo) => void;
  onDelete?: (camera: ICameraInfo) => void;
  onUpdate?: (camera: ICameraInfo) => void;
  onRebootCamera?: (camera: ICameraInfo) => void;
}

export const ScreenModeToIndex: { [key in ScreenMode]: number } = {
  playback: 0,
  'live-view': 1,
  notifications: 2,
  'camera-cloud-storage': 3,
  report: 4,
  'gps-tracking': 5,
  settings: 6,
  'cloud-storage': 7,
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      flexDirection: 'column',
      flexGrow: 1,
      height: 'calc(var(--vh, 1vh) * 100 - 56px - 165px)',
      // mantis :  8831 - KimSeongHyun
      // Scroll Top을 위해 margin을 padding으로 수정
      paddingTop: (props: any) =>
        props.mode === 'cloud-storage'
          ? 70
          : 48 + (props.headerDivRef?.offsetHeight ?? 56),
      // marginTop: (props: ScreenDefaultProps) =>
      //   48 + (props.headerDivRef?.offsetHeight ?? 56),
      [theme.breakpoints.up(Webviewer.mobile)]: {
        height: (props: ScreenDefaultProps) =>
          props.error
            ? 'calc(var(--vh, 1vh) * 100 - 85px - 52px)'
            : 'calc(var(--vh, 1vh) * 100 - 56px - 52px)',
        // margin: theme.spacing(5, 4, 2, 4),
        // mantis :  8831 - KimSeongHyun
        // Scroll Top을 위해 margin을 padding으로 수정
        paddingTop: (props: any) =>
          props.mode === 'cloud-storage' ? 70 : props.error ? 48 + 85 : 48 + 56,
        // marginTop: (props: ScreenDefaultProps) =>
        //   props.error ? 48 + 85 : 48 + 56,
      },
    },
    tabDiv: {
      backgroundColor: (props: ScreenDefaultProps) =>
        props.color === 'dark'
          ? SemanticDarkColors.primary['1']
          : props.colors.primary['0'],
      top: (props: ScreenDefaultProps) =>
        props.headerDivRef?.offsetHeight ?? 56,
      ...(theme.direction === 'rtl'
        ? { left: 0, right: 0 }
        : { left: 235, right: 0 }),
      position: 'fixed',
      zIndex: 90,
      height: 53 + 29,
      display: 'flex',
      alignItems: 'flex-end',
      [theme.breakpoints.up(Webviewer.mobile)]: {
        padding: theme.spacing(0, 4),
        height: 53,
        ...(theme.direction === 'rtl'
          ? { left: 0, right: 235 }
          : { left: 235, right: 0 }),
      },
    },
    //@ts-ignore
    tabScroll: {
      borderBottom: (props: ScreenDefaultProps) =>
        `1px solid ${props.colors.primary['5']}`,
      overflowX: 'scroll',
      '&::-webkit-scrollbar': {
        display: 'none',
      },
      [theme.breakpoints.up(Webviewer.mobile)]: {
        overflowX: 'unset',
        width: '100%',
      },
    },
    tab: {
      display: 'flex',
      // justifyContent: "space-between",
      minWidth: 570,
      width: '100%',
      height: 52,
      alignItems: 'flex-end',
      [theme.breakpoints.down(Webviewer.mobile)]: {
        minWidth: 'auto',
      },
    },
    tabDivClosed: {
      left: 73,
    },
    tabDivMobile: {
      left: 0,
      flexDirection: 'column',
      alignItems: 'unset',
    },
    tabItem: {
      display: 'flex',
      alignItems: 'flex-end',
      paddingBottom: 6,
      whiteSpace: 'nowrap',
      color: (props: ScreenDefaultProps) => props.colors.primary['1'],
    },
    mobileCamNameDiv: {
      display: 'flex',
      paddingTop: theme.spacing(1),
      justifyContent: 'center',
    },
    downUploadModal: {
      position: 'fixed',
      right: 0,
      bottom: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'end',
      zIndex: 99,
      [theme.breakpoints.up(Webviewer.mobile)]:
        theme.direction === 'rtl'
          ? {
              width: 437,
              left: 32,
            }
          : {
              width: 437,
              right: 32,
            },
    },
    selectRoot: {
      maxWidth: 343,
      ...(theme.direction === 'rtl' ? { marginRight: 32 } : { marginLeft: 32 }),
      '& .MuiPaper-root': {
        border: 'none',
        maxWidth: 343,
        overflowX: 'hidden',
        maxHeight: '70vh',
      },
    },
    selectPaper: {
      width: 343,
      padding: 8,
    },

    scrollBar: {
      '& > div:first-of-type': {
        ...(theme.direction === 'rtl'
          ? { marginLeft: -15, marginRight: `0px !important` }
          : { marginRight: -15 }),
      },
    },
  })
);

const initCamItem = {
  key: '',
  value: '',
};

export const CLOUD_ONBOARDING_TOUR = 'pitta-cloud-storage-tour';

export const CameraPlaybackScreen = (
  props: CameraPlaybackScreenProps & PermissionProps & ScreenDefaultProps
) => {
  const {
    mode,
    psn,
    openMenu,
    onRename,
    onDelete,
    onUpdate,
    onRebootCamera,
    ...permissions
  } = props;

  const {
    gpsTrackingPerm,
    reportPerm,
    settingsCamPerm,
    fotaCamPerm,
    deleteCamPerm,
    liveviewPerm,
    rebootCamPerm,
  } = permissions;

  const classes = useStyles(props);
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation<{
    state?: 'fleeta-drive' | 'live-event-upload';
  }>();
  const theme = useTheme();
  const { t } = useTranslation();
  const mobileMedia = useMediaQuery(theme.breakpoints.down(Webviewer.mobile));
  const { fullscreen, menuOpen } = useSelector(
    (state: RootState) => state[THEME]
  );
  const { downloadVODList } = useSelector((state: RootState) => state[VOD]);
  const { subscriptionInfo } = useSelector(
    (state: RootState) => state[PAYMENT]
  );
  const [mobile, setMobile] = useState(mobileMedia);
  const [notificationCam, setNotificationCam] = useState<ICameraInfo>();
  const [openNotification, setOpenNotification] = useState(false);
  const [openEditProfile, setOpenEditProfile] = useState(false);

  const [searchKey, setSearchKey] = useState<string>(''); 
  const tabRefs = useRef<(HTMLElement | null)[]>([]);
  const tabScrollRef = useRef<HTMLDivElement>(null); 

  const { isOwner } = useRole();

  useEffect(() => {
    if (location.pathname.includes('playback')) {
      const scrollContainer = tabScrollRef.current;
      const cameraMenu = tabRefs.current[0];

      if (scrollContainer && cameraMenu) {
        scrollContainer.scrollTo({
          left: cameraMenu.offsetLeft,
          behavior: 'smooth',
        });
      }
    }

    if (location.pathname.includes('live-view')) {
      const scrollContainer = tabScrollRef.current;
      const cameraMenu = tabRefs.current[1];

      if (scrollContainer && cameraMenu) {
        scrollContainer.scrollTo({
          left: cameraMenu.offsetLeft,
          behavior: 'smooth',
        });
      }
    }

    if (location.pathname.includes('cloud-storage')) {
      const scrollContainer = tabScrollRef.current;
      const cameraMenu = tabRefs.current[3];

      console.log('cameraMenu.offsetLeft', cameraMenu?.offsetLeft);

      if (scrollContainer && cameraMenu) {
        scrollContainer.scrollTo({
          left: cameraMenu.offsetLeft,
          behavior: 'smooth',
        });
      }
    }

    if (location.pathname.includes('report')) {
      const scrollContainer = tabScrollRef.current;
      const cameraMenu = tabRefs.current[4];

      if (scrollContainer && cameraMenu) {
        scrollContainer.scrollTo({
          left: cameraMenu.offsetLeft,
          behavior: 'smooth',
        });
      }
    }

    if (location.pathname.includes('gps-tracking')) {
      const scrollContainer = tabScrollRef.current;
      const cameraMenu = tabRefs.current[5];

      if (scrollContainer && cameraMenu) {
        scrollContainer.scrollTo({
          left: cameraMenu.offsetLeft,
          behavior: 'smooth',
        });
      }
    }
  }, [location]);

  useEffect(() => {
    if (!location.state?.state) return;

    if (location.state.state === 'fleeta-drive') {
      setValue(1);
    } else {
      setValue(2);
    }
  }, [location.state?.state]);

  useEffect(() => {
    if (!fullscreen) {
      setMobile(mobileMedia);
    }
  }, [fullscreen, mobileMedia]);

  const match = useRouteMatch<{ psn: string }>('/cameras/:psn');

  const cameraState = useSelector((state: RootState) => state[CAMERA]);
  const { cameraList, firmware, firmwares } = cameraState;

  const userState = useSelector((state: RootState) => state[USER]);
  const { email, loginInfo } = userState;

  const { progressDisplayOrder } = useSelector(
    (state: RootState) => state[VOD]
  );
  const { multiliveview } = useSelector((state: RootState) => state[LIVEVIEW]);

  const [value, setValue] = useState(ScreenModeToIndex[mode]);

  const [openUnpairBattery, setOpenUnpairBattery] = useState(false);
  const [unpairBatteryCam, setUnpairBatteryCam] = useState<ICameraInfo>();

  const [openCamMenu, setOpenCamMenu] = useState(false);
  const [currentCam, setCurrentCam] = useState<ICameraInfo>();
  const [camList, setCamList] = useState<ISelectorItem[]>([initCamItem]);

  const [backScroll /*, setBackScroll*/] = React.useState(false);
  const [openTourModal, setOpenTourModal] = React.useState(false);
  const [openCancelModal, setOpenCancelModal] = useState(false);

  const [availableNewFws, setAvailableNewFws] = useState<AvailableFws>({});
  const [nextLocation, setNextLocation] = useState<Location>();

  const { data: usageList } = useQuery({
    queryKey: ['usage-account', email],
    enabled: !!email && !!subscriptionInfo,
    queryFn: () => {
      console.log(
        'usage-account',
        'email1',
        email,
        'subscriptionInfo',
        subscriptionInfo
      );
      if (!email) return;

      if (isOwner) {
        return getCloudStorageUsage({
          email: email,
        });
      } else {
        const psnList =
          cameraList?.deviceListInfo?.map((c) => c.device.psn) ?? [];

        return getCloudStorageUsage({
          email: subscriptionInfo?.masterEmail ?? '',
          psnList,
        });
      }
    },
  });

  const unblockRef = useRef<() => void>(() => {});

  useEffect(() => {
    unblockRef.current = history.block((tx) => {
      // tx는 {pathname, search} 같은 location 정보
      // 페이지를 떠나려 할 때 커스텀 모달 띄우기
      // setNextLocation(tx);     // 사용자가 이동하려 한 경로 정보 저장
      // setModalVisible(true);   // 모달 열기
      const isDownloading = _.filter(
        downloadVODList,
        (v) => !v.failed && !v.cancelled && !v.downloadDone
      );
      console.log('isDownloading', isDownloading);
      console.log('unblock', tx);

      if (isDownloading.length > 0) {
        if (tx.pathname.includes(`/cameras/${currentCam?.psn}`)) {
        } else {
          //@ts-ignore
          setNextLocation(tx); // 사용자가 이동하려 한 경로 정보 저장

          setOpenCancelModal(true);
          return false; // 항상 false를 반환해서 브라우저 기본 이동을 막음
        }
      }
    });

    // 컴포넌트 언마운트될 때 block 해제
    return () => {
      unblockRef.current?.();
    };
  }, [currentCam?.psn, downloadVODList, history]);

  useEffect(() => {
    if (mode !== 'cloud-storage') return;

    const tourString = localStorage.getItem(CLOUD_ONBOARDING_TOUR);
    const tour = JSON.parse(tourString ?? '[]');

    if (!_.includes(tour, email)) {
      setOpenTourModal(true);
    }
  }, [email, mode]);

  useEffect(() => {
    const body = document.getElementById('body-container');
    body?.setAttribute('style', '');
  });

  // useEffect(() => {
  //   if (!camera && cameraList) {
  //     dispatch(loadCamera(psn));
  //   }
  // }, [camera, cameraList, dispatch, psn]);

  useEffect(() => {
    const cam = _.chain(cameraList?.deviceListInfo)
      .map((c) => c.device)
      .find((c) => c.psn === psn)
      .value();

    setCurrentCam((c) => {
      if (c?.psn !== cam?.psn) {
        return cam;
      } else if (c?.active !== cam?.active) {
        return cam;
        // mantis - 11504, cam rename하면 모바일 상단 카메라명 변경되도록 수정 (Leehj)
      } else if (c?.dev_name !== cam?.dev_name) {
        return cam;
      }
      return c;
    });
    dispatch(setCameraInfo(cam));
  }, [psn, cameraList?.deviceListInfo, dispatch]);

  useEffect(() => {
    if (currentCam) {
      dispatch(loadFirmwareConfig(currentCam));
    }
  }, [currentCam, dispatch]);

  useEffect(() => {
    if (currentCam && firmware?.psn !== currentCam.psn) {
      const load = () => {
        dispatch(loadFirmwareSettings(currentCam.psn));
      };

      const timer = setTimeout(load, 500);
      return () => {
        clearTimeout(timer);
      };
    }
  }, [currentCam, dispatch, firmware?.psn]);

  useEffect(() => {
    if (cameraList) {
      setAvailableNewFws(
        _.reduce(
          cameraList.deviceListInfo,
          (result, dev) => {
            return {
              ...result,
              [dev.device.psn]: findNewFw(dev.device, firmwares),
            };
          },
          {}
        )
      );
    }
  }, [cameraList, firmwares]);

  useEffect(() => {
    function tick() {
      if (currentCam) {
        dispatch(loadVODUploadList(currentCam));
      }
    }
    if (currentCam && currentCam.active === 'on') {
      let timer: NodeJS.Timeout | null = null;
      let id = setInterval(tick, UPLOADING_QUEUE_UPDATE_INTERVAL);
      if (location.pathname.indexOf('live-view') > -1) {
        timer = setTimeout(tick, 500);
      } else {
        tick();
      }
      return () => {
        clearInterval(id);
        if (timer) {
          clearTimeout(timer);
        }
      };
    }
  }, [dispatch, currentCam, location]);

  useEffect(() => {
    if (currentCam?.psn !== psn) {
      if (psn === 'none' && currentCam?.psn) {
        dispatch(loadCamera(currentCam?.psn));
      } else {
        dispatch(loadCamera(psn));
      }
    }
  }, [currentCam?.psn, dispatch, psn]);

  useEffect(() => {
    if (!cameraList) {
      dispatch(loadCameras());
    }
  }, [cameraList, dispatch]);

  useEffect(() => {
    setValue(ScreenModeToIndex[mode]);
  }, [mode]);

  // mantis : 8831 - KimSeongHyun
  // 탭 이동시 스크롤 최상위로 이동
  const scrollToTop = useCallback(() => {
    document.getElementById('scrollTop')?.scrollIntoView(true);
  }, []);

  // value 값에 따라 선택된 탭으로 스크롤
  useEffect(() => {
    if (mobile && tabRefs.current[value] && tabScrollRef.current) {
      const tabElement = tabRefs.current[value];
      const scrollContainer = tabScrollRef.current;
      
      if (tabElement) {
        const tabRect = tabElement.getBoundingClientRect();
        const containerRect = scrollContainer.getBoundingClientRect();
        
        // 탭이 컨테이너 내에서 보이는지 확인
        const isVisible = 
          tabRect.left >= containerRect.left && 
          tabRect.right <= containerRect.right;
        
        // 탭이 화면에 보이지 않을 때만 스크롤
        if (!isVisible) {
          // 스크롤 컨테이너 내에서의 상대적 위치 계산
          const tabOffsetLeft = tabElement.offsetLeft;
          const containerOffsetLeft = scrollContainer.offsetLeft;
          
          // 선택된 탭을 왼쪽으로 스크롤
          const scrollOffset = tabOffsetLeft - containerOffsetLeft;
          let targetScrollLeft = scrollContainer.scrollLeft + scrollOffset;            
          
          // 스크롤 실행
          scrollContainer.scrollTo({
            left: targetScrollLeft,
            behavior: 'smooth'
          });
        }
      }
    }
  }, [value, mobile]);

  const handleRebbotCam = useMemo(() => {
    return rebootCamPerm
      ? () => {
          currentCam && onRebootCamera?.(currentCam);
          setOpenCamMenu(false);
        }
      : null;
  }, [currentCam, onRebootCamera, rebootCamPerm]);

  const handleFOTA = useCallback(() => {
    if (!currentCam || !fotaCamPerm) return null;

    if (_.find(availableNewFws, (v, k) => k === currentCam?.psn)?.blackvue) {
      history.push(`/cameras/${currentCam.psn}/fota`);
    } else if (
      _.find(availableNewFws, (v, k) => k === currentCam?.psn)?.dmc200
    ) {
      history.push(`/cameras/${currentCam.psn}/fota-dmc200`);
    } else {
      dispatch(openToast({ message: 'Firmware is up_' }));
      setOpenCamMenu(false);
    }
  }, [availableNewFws, currentCam, dispatch, fotaCamPerm, history]);

  const handleDelete = useMemo(() => {
    return deleteCamPerm
      ? () => {
          setOpenCamMenu(false);
          currentCam && onDelete?.(currentCam);
        }
      : null;
  }, [currentCam, deleteCamPerm, onDelete]);

  const handleChangeCurrentCam = (cam: ICameraInfo) => {
    setCurrentCam(cam);
  };

  const handleSettings: (() => void) | null = useMemo(() => {
    return settingsCamPerm
      ? () => {
          console.log('moreCam', currentCam);
          if (currentCam) {
            if (currentCam.require_time_sync)
              dispatch(setCameraTimeSync(currentCam.require_time_sync));
            history.push(`/cameras/${currentCam.psn}/settings`);
          }
          setOpenCamMenu(false);
        }
      : null;
  }, [history, currentCam, settingsCamPerm, dispatch]);

  const handleUnpairBattery = (camera: ICameraInfo) => {
    setOpenUnpairBattery(true);
    setUnpairBatteryCam(camera);
    setOpenCamMenu(false);
  };

  useEffect(() => {
    if (cameraList?.deviceListInfo) {
      const camList = cameraList?.deviceListInfo
        .filter((c) => {
          const isNoneCloudModel = _.includes(
            _.filter(OLD_MODELS, (d) => !ENABLED_CLOUD_OLD_MODELS.includes(d)),
            c.device.model
          );

          if (getPatialUnsupportModel(c.device)) {
            return false;
          }

          if (isNoneCloudModel) {
            return false;
          }

          return c.device.valid === 'valid';
        })
        .map((c) => {
          const usageData = usageList?.find((u) => u.psn === c.device.psn);

          if (!usageData) {
            return {
              key: c.device.psn,
              value: c.device.dev_name,
            };
          }

          return {
            key: c.device.psn,
            value:
              c.device.dev_name +
              '\n' +
              `Cloud Storage: ${usageData.usage}/${usageData.limit}GB`,
          };
        });
      setCamList([initCamItem, ...camList]);
    }
  }, [cameraList, email, isOwner, subscriptionInfo?.masterEmail, usageList]);

  const ableCloudCamList = useMemo(() => {
    return _.filter(cameraList?.deviceListInfo, (c) => {
      const isNoneCloudModel = _.includes(
        _.filter(OLD_MODELS, (d) => !ENABLED_CLOUD_OLD_MODELS.includes(d)),
        c.device.model
      );
      if (getPatialUnsupportModel(c.device)) {
        return false;
      }

      if (isNoneCloudModel) {
        return false;
      }

      return true;
    });
  }, [cameraList?.deviceListInfo]);

  useEffect(() => {
    const newCamList = _.filter(ableCloudCamList, (c) => {
      const isNoneCloudModel = _.includes(
        _.filter(OLD_MODELS, (d) => !ENABLED_CLOUD_OLD_MODELS.includes(d)),
        c.device.model
      );
      if (getPatialUnsupportModel(c.device)) {
        return false;
      }

      if (isNoneCloudModel) {
        return false;
      }

      return (
        c.device.dev_name.toLowerCase().includes(searchKey.toLowerCase()) &&
        c.device.valid === 'valid'
      );
    }).map((c) => {
      const usageData = usageList?.find((u) => u.psn === c.device.psn);

      if (!usageData) {
        return {
          key: c.device.psn,
          value: c.device.dev_name,
        };
      }

      return {
        key: c.device.psn,
        value:
          c.device.dev_name +
          '\n' +
          `Cloud Storage: ${usageData.usage}/${usageData.limit}GB`,
      };
    });
    setCamList([initCamItem, ...newCamList]);
  }, [ableCloudCamList, searchKey, usageList]);

  const bodyMarkup = useMemo(() => {
    return (
      <>
        {mode === 'cloud-storage' && (
          <div className={classes.selectRoot}>
            <Selector
              onType={(string) => {
                setSearchKey(string);
              }}
              onChange={(item) => {
                const cam = _.find(cameraList?.deviceListInfo, (c) => {
                  return c.device.psn === item.key;
                })?.device;
                if (cam) {
                  handleChangeCurrentCam(cam);
                }
              }}
              dense
              t={t}
              select={false}
              menuItemClassName={classes.selectPaper}
              placeholder={t('Select or search the camera')}
              disabled={ableCloudCamList.length < 1}
              items={camList}
              menuScrollTime={true}
            />
          </div>
        )}
        {value === 0 && <PlaybackPanel openMenu={openMenu} {...permissions} />}
        {value === 1 && currentCam && (
          <LiveViewPanelWrapper camera={currentCam} {...permissions} />
        )}
        {value === 3 && (
          <PlaybackPanel
            openMenu={openMenu}
            {...permissions}
            mode='camera-cloud-storage'
          />
        )}
        {value === 5 && gpsTrackingPerm && (
          <GPSTrackingPanel
            plan={props.plan}
            openMenu={props.openMenu}
            {...permissions}
          />
        )}
        {value === 4 && reportPerm && (
          <ReportPanel openMenu={openMenu} {...permissions} />
        )}
        {value === 6 && settingsCamPerm && (
          <CameraSettingPanel {...permissions} />
        )}
        {value === 7 && (
          <PlaybackPanel
            openMenu={openMenu}
            {...permissions}
            mode='cloud-storage'
          />
        )}
      </>
    );
  }, [
    ableCloudCamList.length,
    camList,
    cameraList?.deviceListInfo,
    classes.selectPaper,
    classes.selectRoot,
    currentCam,
    gpsTrackingPerm,
    mode,
    openMenu,
    permissions,
    props.openMenu,
    props.plan,
    reportPerm,
    settingsCamPerm,
    t,
    value,
  ]);

  return (
    // mantis :  8831 - KimSeongHyun
    // 탭 이동시 스크롤 최상위로 이동
    <div className={classes.root} dir={theme.direction} id='scrollTop'>
      {mode !== 'cloud-storage' && (
        <div
          className={clsx(classes.tabDiv, {
            [classes.tabDivClosed]: !openMenu,
            [classes.tabDivMobile]: mobile,
          })}
        >
          {mobile && (
            <div className={classes.mobileCamNameDiv}>
              <Typography
                category='Default'
                variant='SmallBold'
                htmlColor={props.colors.primary['2']}
              >
                {currentCam?.dev_name}
              </Typography>
            </div>
          )}

          <div ref={tabScrollRef} className={classes.tabScroll}>
            <div className={clsx(classes.tab)}>
              <Tabs overFlowX='auto' value={value}>
                <Tab
                  ref={(el) => (tabRefs.current[0] = el)}
                  className={classes.tabItem}
                  label={`${t('Remote Playback')}`}
                  disabled={
                    currentCam?.active === 'off' ||
                    currentCam?.reg_category === 'wifi' ||
                    getPatialUnsupportModel(currentCam)
                  }
                  onClick={() => {
                    history.push(`/cameras/${match?.params.psn}/playback`);
                    // mantis :  8831 - KimSeongHyun
                    // 탭 이동시 스크롤 최상위로 이동
                    scrollToTop();
                  }}
                />

                <Tab
                  ref={(el) => (tabRefs.current[1] = el)}
                  className={classes.tabItem}
                  label={t('Remote Live view')}
                  disabled={
                    (currentCam?.active === 'off' && !multiliveview) ||
                    !liveviewPerm ||
                    currentCam?.reg_category === 'wifi'
                  }
                  onClick={() => {
                    history.push(`/cameras/${match?.params.psn}/live-view`, {
                      clicked: true,
                    });
                    // mantis :  8831 - KimSeongHyun
                    // 탭 이동시 스크롤 최상위로 이동
                    scrollToTop();
                  }}
                />

                <Tab
                ref={(el) => (tabRefs.current[2] = el)}
                  className={classes.tabItem}
                  label={t('Notifications')}
                  disabled={
                    currentCam?.reg_category === 'wifi' || loginInfo?.demo
                  }
                  onClick={() => {
                    setNotificationCam(currentCam);
                    setOpenNotification(true);
                  }}
                />

                <Tab
                  ref={(el) => (tabRefs.current[3] = el)}
                  className={classes.tabItem}
                  disabled={
                    getPatialUnsupportModel(currentCam) || loginInfo?.demo
                  }
                  label={t('Cloud Storage')}
                  onClick={() => {
                    let state;

                    if (currentCam?.reg_category === 'cloud') {
                      state = 'live-event-upload';
                    }

                    history.push(
                      `/cameras/${match?.params.psn}/cloud-storage`,
                      {
                        state,
                      }
                    );
                    // mantis :  8831 - KimSeongHyun
                    // 탭 이동시 스크롤 최상위로 이동
                    scrollToTop();
                  }}
                />
                <Tab
                  ref={(el) => (tabRefs.current[4] = el)}
                  className={classes.tabItem}
                  disabled={
                    !reportPerm ||
                    currentCam?.reg_category === 'wifi' ||
                    _.includes(PSN650, currentCam?.psn.substr(0, 4)) ||
                    loginInfo?.demo
                  }
                  label={t('Report')}
                  onClick={() => {
                    history.push(`/cameras/${match?.params.psn}/report`);
                    // mantis :  8831 - KimSeongHyun
                    // 탭 이동시 스크롤 최상위로 이동
                    scrollToTop();
                  }}
                />

                <Tab
                  ref={(el) => (tabRefs.current[5] = el)}
                  className={classes.tabItem}
                  disabled={
                    !gpsTrackingPerm || currentCam?.reg_category === 'wifi'
                  }
                  label={t('GPS tracking')}
                  onClick={() => {
                    history.push(`/cameras/${match?.params.psn}/gps-tracking`);
                    // mantis :  8831 - KimSeongHyun
                    // 탭 이동시 스크롤 최상위로 이동
                    scrollToTop();
                  }}
                />

                <Tab
                  ref={(el) => (tabRefs.current[6] = el)}
                  className={classes.tabItem}
                  disabled={
                    !settingsCamPerm ||
                    currentCam?.reg_category === 'wifi' ||
                    loginInfo?.demo
                  }
                  label={t('Settings')}
                  onClick={() => {
                    history.push(`/cameras/${match?.params.psn}/settings`);
                    scrollToTop();
                  }}
                />

                <Tab
                  ref={(el) => (tabRefs.current[7] = el)}
                  className={classes.tabItem}
                  label={t('More')}
                  onClick={() => setOpenCamMenu(true)}
                  disabled={loginInfo?.demo}
                />
              </Tabs>
            </div>
          </div>
        </div>
      )}

      <div
        style={
          // 모바일에서 이벤트공유 모달 open시 background가 스크롤 되는현상 방지(HJ)
          mobile && backScroll
            ? { height: '100%', paddingTop: 30, overflow: 'hidden' }
            : mobile
            ? { height: '100%', paddingTop: 30, overflow: 'auto' }
            : { height: '100%' }
        }
      >
        {mobile ? (
          bodyMarkup
        ) : (
          <AutoSizer>
            {({ width, height }) => (
              <Scrollbars
                className={classes.scrollBar}
                style={{
                  height,
                  width: `calc(100vw - ${menuOpen ? 235 : 72}px)`,
                }}
                autoHide
              >
                {bodyMarkup}
              </Scrollbars>
            )}
          </AutoSizer>
        )}
      </div>
      <FleetaCameraMenu
        {...props}
        onClose={() => setOpenCamMenu(false)}
        open={openCamMenu}
        camera={currentCam}
        anchorRef={{ current: tabRefs.current[7] }}
        isHideSettingMenu
        isCloudConnectSettingDisabled
        isFormatSDCardDisabled
        isCameraSettingDisabled={!settingsCamPerm}
        isRemoteFirmwareUpdateDisabled={!fotaCamPerm}
        isRebootCameraDisabled={!isOwner}
        isRemoveCameraDisabled={!isOwner}
        isVisibleConnectDashcamLocally={false}
        placement='top-start'
        newFw={
          !!(
            _.find(availableNewFws, (v, k) => k === currentCam?.psn)
              ?.blackvue ||
            _.find(availableNewFws, (v, k) => k === currentCam?.psn)?.dmc200
          )
        }
        onClickAway={() => setOpenCamMenu(false)}
        onClickMenu={(menu) => {
          if (menu === 'CAMERA_PROFILE') {
            setOpenEditProfile(true);
          }

          if (menu === 'CAMERA_SETTING') {
            handleSettings?.();
          }

          if (menu === 'REMOTE_FIRMWARE_UPDATE') {
            handleFOTA?.();
          }

          if (menu === 'REBOOT_CAMERA' && currentCam) {
            handleRebbotCam?.();
          }

          if (menu === 'UNPAIR_BATTERY' && currentCam) {
            handleUnpairBattery(currentCam);
          }

          if (menu === 'DELETE_CAMERA' && currentCam) {
            handleDelete?.();
          }

          setOpenCamMenu(false);
        }}
      />

      <EditProfileModal
        open={openEditProfile}
        camera={currentCam}
        onClose={() => setOpenEditProfile(false)}
        onSuccess={() => {
          setOpenEditProfile(false);
          dispatch(loadCameras());
        }}
      />
      <UnpairBatteryModal
        open={openUnpairBattery}
        camera={unpairBatteryCam}
        onClose={() => setOpenUnpairBattery(false)}
        onSuccess={() => {
          setOpenUnpairBattery(false);
          dispatch(loadCameras());
        }}
      />
      <NotificationSettingPanel
        open={openNotification}
        onClose={() => setOpenNotification(false)}
        currentCam={notificationCam}
      />
      <CloudStorageTourModal
        openTourModal={openTourModal}
        setOpenTourModal={setOpenTourModal}
      />
      <div
        className={classes.downUploadModal}
        style={{ flexDirection: progressDisplayOrder }}
      >
        <FileUploadModal />
        {/* mantis - 12598, 다운로드 취소하는 경우 진행중인 업로드 취소하기위해 psn 필요 (Leehj) */}
        <FileDownloadModal camera={currentCam} />
      </div>

      <Modal
        open={openCancelModal}
        headingIcon={<ErrorIcon htmlColor={LightColors.secondary['11']} />}
        heading={t('Cancel download?')}
        content={
          <Typography category='Default' variant='Body'>
            {t('Your download is not_')}
          </Typography>
        }
        close
        onClose={() => setOpenCancelModal(false)}
        onClickPositive={() => setOpenCancelModal(false)}
        onClickNegative={() => {
          // “이동” 버튼 클릭 시
          setOpenCancelModal(false);
          // block 해제 후 실제로 이동
          unblockRef.current?.();
          dispatch(clearDownloadVOD());
          if (nextLocation) {
            history.push(nextLocation.pathname);
          }
        }}
        RButton={t('Continue download')}
        LButton={t('Cancel download')}
      />
    </div>
  );
};
